import {
  Box,
  TextField,
  InputAdornment,
  Typography,
  useTheme,
  MenuItem,
  Breadcrumbs,
  Link,
  Checkbox,
  Avatar,
  Pagination,
  Select,
  FormControl,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  useMediaQuery,
  IconButton,
  ListSubheader,
  Tooltip,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import debounce from 'lodash/debounce';
import { GATEWAY_LOGOS, TRTYPE_LOGOS, AGGREGATOR_OPTIONS } from '@/constants/icons';
import { useLazyGetPaymentListQuery } from '@/services/paymentApi';
import { useGetAggregatorsQuery } from '@/services';
import { useGetAmcListQuery } from '@/services/dashboardApi';
import { useGetAssetsDropdownQuery } from '@/services/assetApi';
import { useSelector } from 'react-redux';
import type { RootState } from '@/store/store';
import { UserRole } from '@/types/user';

interface AssetDropdownItem {
  id: string;
  name: string;
}

interface AmcAssetGroup {
  amcId: string;
  amcName: string;
  assets: AssetDropdownItem[];
}

interface AssetsDropdownResponse {
  data: AmcAssetGroup[];
}

const emptyArray: string[] = [];

export default function PaymentList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { data: amcData = [] } = useGetAmcListQuery();
  const role = useSelector((state: RootState) => state.user.role) as UserRole | undefined;
  const isSuperAdmin = role === UserRole.ADMIN;
  const isAmcScopedUser = role === UserRole.AMC || role === UserRole.AMC_ADMIN;

  const searchParamsValues = useMemo(
    () => ({
      page: parseInt(searchParams.get('page') || '1', 10),
      pageSize: parseInt(searchParams.get('pageSize') || '10', 10),
      searchTerm: searchParams.get('search') || '',
      statusFilter: searchParams.get('status') || '',
      amcFilter: searchParams.get('amc') ? searchParams.get('amc')!.split(',') : emptyArray,
      transactionTypeFilter: searchParams.get('transactionType')
        ? searchParams.get('transactionType')!.split(',')
        : emptyArray,
      aggregatorFilter: searchParams.get('aggregator')
        ? searchParams.get('aggregator')!.split(',')
        : emptyArray,
      paymodeFilter: searchParams.get('paymode')
        ? searchParams.get('paymode')!.split(',')
        : emptyArray,
      assetFilter: searchParams.get('asset') ? searchParams.get('asset')!.split(',') : emptyArray,
    }),
    [searchParams]
  );

  const {
    page,
    pageSize,
    searchTerm,
    statusFilter,
    amcFilter,
    transactionTypeFilter,
    aggregatorFilter,
    paymodeFilter,
    assetFilter,
  } = searchParamsValues;

  // Use backend /assets/dropdown for asset options.
  // For ADMIN, pass selected AMC so dropdown is scoped to that AMC.
  // For AMC users, backend will infer AMC from auth token when amcId is omitted.
  const { data: assetDropdownData } = useGetAssetsDropdownQuery(
    isSuperAdmin ? amcFilter?.[0] || undefined : undefined,
    {
      skip: isSuperAdmin && !amcFilter?.[0],
    }
  ) as { data?: AssetsDropdownResponse };
  // Group assets by AMC for the dropdown
  const groupedAssetOptions = useMemo(() => {
    if (!assetDropdownData?.data) return [];

    // Check if the data is already grouped by the backend
    const firstItem = assetDropdownData.data[0] as any;
    if (firstItem && 'assets' in firstItem && Array.isArray(firstItem.assets)) {
      return (assetDropdownData.data as any).map((amc: any) => ({
        amcId: amc.amcId,
        amcName: amc.amcName,
        assets: amc.assets,
      })) as AmcAssetGroup[];
    }

    // Otherwise, it's an array of Asset objects, so we group them manually
    const assets = assetDropdownData.data as any[];
    const groups: Record<string, AmcAssetGroup> = {};

    assets.forEach((asset) => {
      // Fallback to selected AMC ID if asset.amc.id is missing
      const amcId = asset.amc?.id || amcFilter?.[0] || 'unknown';
      if (!groups[amcId]) {
        const amcName = amcData.find((a) => a.id === amcId)?.name || 'Assets';
        groups[amcId] = {
          amcId,
          amcName,
          assets: [],
        };
      }
      groups[amcId].assets.push({
        id: asset.id,
        name: asset.name,
      });
    });

    const result = Object.values(groups);
    // If there's only one group and it's the fallback "Assets", we can simplify
    if (result.length === 1 && result[0].amcName === 'Assets') {
      return result;
    }

    return result;
  }, [assetDropdownData, amcData, amcFilter]);
  const [gridPageSize, setGridPageSize] = useState(pageSize);

  useEffect(() => {
    setGridPageSize(pageSize);
  }, [pageSize]);

  const [searchInputValue, setSearchInputValue] = useState(searchTerm);
  const [debouncedSearchValue, setDebouncedSearchValue] = useState(searchTerm);

  const [totalCount, setTotalCount] = useState(0);

  const updateSearchParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        Object.entries(updates).forEach(([key, value]) => {
          if (value === null || value === '' || value === undefined) {
            newParams.delete(key);
          } else {
            newParams.set(key, value);
          }
        });
        return newParams;
      });
    },
    [setSearchParams]
  );

  // Fetch dynamic aggregators for the selected AMC
  const { data: aggregatorsData } = useGetAggregatorsQuery(amcFilter?.[0] || undefined);

  const [filterOption, setFilterOption] = useState({
    amc: [] as {
      id: string;
      name: string;
      logoPath?: string;
    }[],
    transactionTypes: ['Lumpsum', 'Emandate', 'Hybrid'],
    gateway: [] as string[],
    status: ['success', 'pending', 'failure', 'settled'],
    paymodes: ['NetBanking', 'Debit Card', 'UPI', 'OTM', 'UPIAutopay', 'Enach', 'ISIP'],
  });

  useEffect(() => {
    setFilterOption((prev) => ({
      ...prev,
      amc: (amcData || []).map((amc) => ({
        id: amc.id,
        name: amc.name,
        // normalize null to undefined to satisfy the filterOption type
        logoPath: amc.logoPath || undefined,
      })),
    }));
  }, [amcData]);

  // Update gateway options when aggregators data changes
  useEffect(() => {
    if (aggregatorsData?.aggregators && aggregatorsData.aggregators.length > 0) {
      setFilterOption((prev) => ({
        ...prev,
        gateway: aggregatorsData.aggregators.map((agg) => agg.value),
      }));
    } else {
      // Fallback to hardcoded options
      setFilterOption((prev) => ({
        ...prev,
        gateway: AGGREGATOR_OPTIONS.map((agg) => agg.value),
      }));
    }
  }, [aggregatorsData]);

  const [getPaymentList] = useLazyGetPaymentListQuery();

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const handlePageChange = useCallback(
    (newPage: number) => {
      updateSearchParams({ page: (newPage + 1).toString() });
    },
    [updateSearchParams]
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      updateSearchParams({ pageSize: newPageSize.toString(), page: '1' });
      setGridPageSize(newPageSize);
    },
    [updateSearchParams]
  );

  const handleResetFilters = () => {
    updateSearchParams({
      search: null,
      status: 'all',
      amc: null,
      transactionType: null,
      aggregator: null,
      asset: null,
      page: '1',
      pageSize: '20',
    });
    // Clear the local search input state
    setSearchInputValue('');
  };

  const getFailureInfo = (row: any) => {
    if (!row?.status || row.status.toLowerCase() !== 'failure') {
      return null;
    }
    const code = row.swiftFlowErrorCode?.trim();
    const msg = row.message?.trim();
    if (!code && !msg) {
      return null;
    }
    return {
      message: msg || code || '',
      code,
    };
  };

  // Render filters inline (not as a React Component) to avoid remounts that steal focus
  const renderFilterSection = () => {
    const handleStatusChange = (option: string) => {
      updateSearchParams({
        status: option,
        page: '1',
      });
    };
    const handleAmcChange = (option: string) => {
      updateSearchParams({
        amc: option === '' ? null : option,
        page: '1',
      });
    };

    const handleTransactionTypeChange = (option: string) => {
      updateSearchParams({
        transactionType: option === '' ? null : option,
        page: '1',
      });
    };

    const handleAggregatorChange = (option: string) => {
      updateSearchParams({
        aggregator: option === '' ? null : option,
        page: '1',
      });
    };

    const handlePaymodeChange = (option: string) => {
      updateSearchParams({
        paymode: option === '' ? null : option,
        page: '1',
      });
    };

    const handleAssetChange = (option: string) => {
      updateSearchParams({
        asset: option === '' ? null : option,
        page: '1',
      });
    };

    return (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
          alignItems: 'flex-start',
        }}
      >
        {/* Status Filter */}
        <Box sx={{ flex: isMobile ? '1 1 100%' : '1 1 200px', minWidth: 200 }}>
          <TextField
            select
            fullWidth
            size="small"
            label="Status"
            value={statusFilter || 'all'}
            onChange={(e) => handleStatusChange(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            {filterOption.status.map((option) => (
              <MenuItem key={option} value={option} sx={{ textTransform: 'capitalize' }}>
                {option.charAt(0).toUpperCase() + option.slice(1)}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box sx={{ flex: isMobile ? '1 1 100%' : '1 1 200px', minWidth: 200 }}>
          <TextField
            select
            fullWidth
            size="small"
            label="Paymode"
            value={paymodeFilter?.[0] || ''}
            onChange={(e) => handlePaymodeChange(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {filterOption.paymodes.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        {/* AMC filter: hide for AMC users, show for others (e.g. Admin) */}
        {!isAmcScopedUser && (
          <Box sx={{ flex: isMobile ? '1 1 100%' : '1 1 200px', minWidth: 200 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="AMC"
              value={filterOption.amc.length > 0 ? amcFilter?.[0] || '' : ''}
            >
              <MenuItem value="" onClick={() => handleAmcChange('')}>
                All
              </MenuItem>
              {filterOption.amc.map((option) => {
                return (
                  <MenuItem
                    key={option.id ?? ''}
                    value={option.id ?? ''}
                    onClick={() => option && handleAmcChange(option.id)}
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        height: '20px',
                      }}
                    >
                      <Checkbox checked={option ? amcFilter?.includes(option.id) : false} />
                      {option?.logoPath ? (
                        <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                          <img
                            style={{ height: '24px', width: '24px' }}
                            src={option.logoPath}
                            alt=""
                          />
                        </Box>
                      ) : (
                        <Avatar
                          sizes="small"
                          sx={{ height: '24px', width: '24px', marginRight: 2 }}
                        >
                          {option?.name.charAt(0).toUpperCase()}
                        </Avatar>
                      )}
                      {option?.name}
                    </Box>
                  </MenuItem>
                );
              })}
            </TextField>
          </Box>
        )}

        {/* Asset filter: visible for all roles; options derived from payments data */}
        <Box sx={{ flex: isMobile ? '1 1 100%' : '1 1 200px', minWidth: 200 }}>
          <TextField
            select
            fullWidth
            size="small"
            label="Asset"
            value={assetFilter?.[0] || ''}
            onChange={(e) => handleAssetChange(e.target.value)}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  sx: { maxHeight: 400 },
                },
              },
            }}
          >
            <MenuItem value="">All</MenuItem>
            {groupedAssetOptions.map((group) => {
              const showSubheader = groupedAssetOptions.length > 1 || group.amcName !== 'Assets';
              return [
                showSubheader && (
                  <ListSubheader
                    key={group.amcId}
                    sx={{ fontWeight: 'bold', color: 'text.primary' }}
                  >
                    {group.amcName}
                  </ListSubheader>
                ),
                ...group.assets.map((asset) => (
                  <MenuItem key={asset.id} value={asset.id} sx={{ pl: showSubheader ? 4 : 2 }}>
                    {asset.name}
                  </MenuItem>
                )),
              ];
            })}
          </TextField>
        </Box>

        <Box sx={{ flex: isMobile ? '1 1 100%' : '1 1 200px', minWidth: 200 }}>
          <TextField
            select
            fullWidth
            size="small"
            label="Transaction Type"
            value={transactionTypeFilter?.join(',') || ''}
          >
            <MenuItem value="" onClick={() => handleTransactionTypeChange('')}>
              All
            </MenuItem>
            {filterOption.transactionTypes.map((option) => (
              <MenuItem
                key={option}
                value={option}
                onClick={() => handleTransactionTypeChange(option)}
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', height: '20px' }}>
                  <Checkbox checked={transactionTypeFilter?.includes(option)} size="small" />
                  {option}
                </Box>
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box sx={{ flex: isMobile ? '1 1 100%' : '1 1 200px', minWidth: 200 }}>
          <TextField
            select
            fullWidth
            size="small"
            label="Payment Aggregator"
            value={aggregatorFilter?.[0] || ''}
          >
            <MenuItem value="" onClick={() => handleAggregatorChange('')}>
              All
            </MenuItem>
            {filterOption.gateway.map((option) => (
              <MenuItem
                key={option}
                value={option}
                onClick={() => handleAggregatorChange(option)}
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <Box
                  fontSize={14}
                  sx={{ display: 'flex', alignItems: 'center', width: '100%', height: '20px' }}
                >
                  <Checkbox checked={aggregatorFilter?.includes(option)} />
                  {option}
                </Box>
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box sx={{ flex: isMobile ? '1 1 100%' : '1 1 300px', minWidth: 200 }}>
          <TextField
            placeholder="Search Transaction / PAN"
            variant="outlined"
            size="small"
            fullWidth
            value={searchInputValue}
            onChange={(e) => {
              const value = e.target.value;
              setSearchInputValue(value);
              debouncer(value);
            }}
            onBlur={() => {
              // Commit the current value to URL on blur
              updateSearchParams({ search: searchInputValue || null, page: '1' });
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                updateSearchParams({ search: searchInputValue || null, page: '1' });
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {searchInputValue ? (
                    <IconButton
                      aria-label="clear search"
                      onClick={() => {
                        // Clear the local input and URL param
                        setSearchInputValue('');
                        setDebouncedSearchValue('');
                        try {
                          // cancel any pending debounce
                          debouncer.cancel();
                        } catch (e) {
                          /* ignore */
                        }
                        updateSearchParams({ search: null, page: '1' });
                      }}
                      edge="end"
                      size="small"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  ) : null}
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>
    );
  };

  // Debounce local input for data fetching and URL updates (like bank-details page)
  const debouncer = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearchValue(value);
        // Update URL only after user stops typing
        updateSearchParams({ search: value || null, page: '1' });
      }, 500),
    [updateSearchParams]
  );

  // Keep local input in sync if searchTerm in URL changes externally
  useEffect(() => {
    setSearchInputValue(searchTerm);
    setDebouncedSearchValue(searchTerm);
  }, [searchTerm]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncer.cancel();
    };
  }, [debouncer]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const queryParams = {
          search: debouncedSearchValue,
          status: statusFilter === 'all' ? undefined : statusFilter,
          // For Admin (non AMC-scoped users), allow explicit AMC filtering.
          // For AMC users, let backend infer AMC from auth token.
          amcId: isAmcScopedUser ? undefined : amcFilter?.[0],
          trtype: transactionTypeFilter?.[0],
          gateway: aggregatorFilter?.[0],
          paymode: paymodeFilter?.[0],
          assetId: assetFilter?.[0],
          page,
          limit: pageSize,
        };

        const res = await getPaymentList(queryParams);
        if (res.data) {
          const data = res.data.data || [];
          setRows(data);
          setTotalCount(res.data.meta?.total || 0);
          const backendLimit = res.data.meta?.limit;
          if (backendLimit && backendLimit !== pageSize) {
            updateSearchParams({ pageSize: backendLimit.toString() });
          }
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    getPaymentList,
    page,
    pageSize,
    debouncedSearchValue,
    statusFilter,
    amcFilter,
    transactionTypeFilter,
    aggregatorFilter,
    paymodeFilter,
    assetFilter,
  ]);

  const StatusBadge = ({ status }: { status: string }) => {
    const colors = {
      success: { bg: theme.palette.success.light, color: theme.palette.success.main },
      settled: { bg: theme.palette.success.light, color: theme.palette.success.main },
      pending: { bg: theme.palette.warning.light, color: theme.palette.warning.main },
      failure: { bg: theme.palette.error.light, color: theme.palette.error.main },
      failed: { bg: theme.palette.error.light, color: theme.palette.error.main },
    };
    const normalized = status.toLowerCase();
    const style = colors[normalized as keyof typeof colors] || colors.pending;

    return (
      <Box
        sx={{
          backgroundColor: style.bg,
          color: style.color,
          borderRadius: '16px',
          padding: '0px 8px', // Updated padding
          display: 'inline-block',
          fontSize: '14px',
          fontWeight: theme.typography.h1.fontWeight, // Added font weight
          height: '30px', // Fixed height
          lineHeight: '28px', // Added minimum width
          textAlign: 'center', // Center the text
        }}
      >
        {status.toLocaleUpperCase()}
      </Box>
    );
  };

  return (
    <Box sx={{ height: 'calc(100vh - 140px)', width: '100%', px: isMobile ? 1 : 2 }}>
      <Breadcrumbs
        separator="·"
        sx={{
          mb: 1,
          '& .MuiBreadcrumbs-separator': {
            mx: 1, // Add some margin around the dot
            color: theme.palette.text.secondary, // Style the dot color
          },
        }}
      >
        <Link
          component={RouterLink}
          to="/dashboard"
          sx={{
            color: theme.palette.primary.main,
            fontSize: theme.typography.subtitle1.fontSize,
            fontWeight: theme.typography.subtitle1.fontWeight,
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          Dashboard
        </Link>
        <Typography variant="subtitle1">Payments</Typography>
      </Breadcrumbs>

      <Box sx={{ backgroundColor: theme.palette.background.paper, borderRadius: '16px', p: 2 }}>
        {renderFilterSection()}

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 2,
          }}
        >
          <Button
            variant="text"
            size="small"
            onClick={handleResetFilters}
            sx={{
              textTransform: 'none',
              alignSelf: isMobile ? 'flex-start' : 'center',
            }}
          >
            Reset Filters
          </Button>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
              justifyContent: isMobile ? 'center' : 'flex-end',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Showing {Math.min((page - 1) * pageSize + 1, totalCount)} to{' '}
              {Math.min(page * pageSize, totalCount)} of {totalCount} entries
            </Typography>
            <FormControl size="small" sx={{ minWidth: 80 }}>
              <Select
                value={gridPageSize}
                onChange={(e) => handlePageSizeChange(parseInt(e.target.value as string, 10))}
                displayEmpty
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              per page
            </Typography>

            <Pagination
              count={Math.ceil(totalCount / pageSize)}
              page={page}
              onChange={(_event, value) => handlePageChange(value - 1)}
              showFirstButton
              showLastButton
              size="small"
            />
          </Box>
        </Box>
      </Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : isMobile ? (
        <Box sx={{ mt: 2 }}>
          {rows.length > 0 ? (
            rows.map((row) => (
              <Paper key={row.id} sx={{ p: 2, mb: 2 }}>
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Link
                    component={RouterLink}
                    to={`/payments/${row.id}`}
                    sx={{ color: theme.palette.primary.main, textDecoration: 'underline' }}
                  >
                    <Typography variant="h6">{row.ihNoOrCart}</Typography>
                  </Link>
                  <StatusBadge status={row.status} />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Folio No
                  </Typography>
                  <Typography variant="body2">
                    {row.folioNo || '-'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Amount
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    ₹ {row.amount}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    AMC / Asset
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {row.amc.logoPath ? (
                        <img
                          src={row.amc.logoPath}
                          alt=""
                          style={{ width: '24px', height: '24px' }}
                        />
                      ) : (
                        <Avatar sx={{ width: '24px', height: '24px', fontSize: '12px' }}>
                          {row.amc.name.charAt(0).toUpperCase()}
                        </Avatar>
                      )}
                      <Typography variant="body2">{row.amc.name}</Typography>
                    </Box>
                    {row.assetName && (
                      <Typography variant="caption" color="text.secondary">
                        {row.assetName}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Transaction Type
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <img
                      src={TRTYPE_LOGOS[row.trtype as keyof typeof TRTYPE_LOGOS]}
                      alt=""
                      style={{ width: '20px', height: '20px' }}
                    />
                    <Typography variant="body2">{row.trtype}</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Aggregator
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <img
                      src={GATEWAY_LOGOS[row.gateway as keyof typeof GATEWAY_LOGOS]}
                      alt=""
                      style={{ width: '20px', height: '20px' }}
                    />
                    <Typography variant="body2">{row.gateway}</Typography>
                  </Box>
                </Box>
                {getFailureInfo(row) && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Message
                    </Typography>
                    {(() => {
                      const info = getFailureInfo(row);
                      if (!info) return null;
                      if (info.code) {
                        return (
                          <Tooltip title={`Code: ${info.code}`} arrow>
                            <Typography variant="body2" sx={{ cursor: 'help' }}>
                              {info.message}
                            </Typography>
                          </Tooltip>
                        );
                      }
                      return <Typography variant="body2">{info.message}</Typography>;
                    })()}
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Initialized At
                  </Typography>
                  <Typography variant="body2">
                    {row.createdAt ? new Date(row.createdAt).toLocaleString('en-IN') : '-'}
                  </Typography>
                </Box>
              </Paper>
            ))
          ) : (
            <Typography align="center" sx={{ py: 3 }}>
              No payments found{searchTerm ? ` for "${searchTerm}"` : ''}
            </Typography>
          )}
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table sx={{ minWidth: 650 }} aria-label="payment table" size="small">
            <TableHead>
              <TableRow>
                <TableCell>Order ID</TableCell>
                <TableCell>Folio No</TableCell>
                <TableCell>AMC / Asset</TableCell>
                <TableCell>Transaction Type</TableCell>
                <TableCell>Payment Aggregator</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="right">Initialized At</TableCell>
                <TableCell align="left">Message</TableCell>
                <TableCell align="right">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length > 0 ? (
                rows.map((row) => (
                  <TableRow
                    hover
                    key={row.id}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell>
                      <Link
                        component={RouterLink}
                        to={`/payments/${row.id}`}
                        sx={{ color: theme.palette.primary.main, textDecoration: 'underline' }}
                      >
                        <Typography variant="button">{row.ihNoOrCart}</Typography>
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" component="span">
                        {row.folioNo || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {row.amc.logoPath ? (
                          <img
                            src={row.amc.logoPath}
                            alt=""
                            style={{ width: '40px', height: '40px' }}
                          />
                        ) : (
                          <Avatar sx={{ width: '35px', height: '35px' }}>
                            {row.amc.name.charAt(0).toUpperCase()}
                          </Avatar>
                        )}
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="subtitle1" component="span">
                            {row.amc.name}
                          </Typography>
                          {row.assetName && (
                            <Typography variant="caption" color="text.secondary" component="span">
                              {row.assetName}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <img src={TRTYPE_LOGOS[row.trtype as keyof typeof TRTYPE_LOGOS]} alt="" />
                        <Typography variant="subtitle1" component="span">
                          {row.trtype}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <img
                          src={GATEWAY_LOGOS[row.gateway as keyof typeof GATEWAY_LOGOS]}
                          alt=""
                        />
                        <Typography variant="subtitle1" component="span">
                          {row.gateway}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">₹ {row.amount}</TableCell>
                    <TableCell align="right">
                      {row.createdAt ? new Date(row.createdAt).toLocaleString('en-IN') : '-'}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const info = getFailureInfo(row);
                        if (!info) return '-';
                        if (info.code) {
                          return (
                            <Tooltip title={`Code: ${info.code}`} arrow>
                              <Typography variant="body2" sx={{ cursor: 'help' }}>
                                {info.message}
                              </Typography>
                            </Tooltip>
                          );
                        }
                        return <Typography variant="body2">{info.message}</Typography>;
                      })()}
                    </TableCell>
                    <TableCell align="right">
                      <StatusBadge status={row.status} />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No payments found{searchTerm ? ` for "${searchTerm}"` : ''}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
